import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  Assignment as AssignmentIcon,
  Cancel as CancelIcon,
  Preview as PreviewIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import TinyMCEEditor from '../TinyMCEEditor';
import { customersAPI, templatesAPI, proposalsAPI } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';
import { useSettings } from '../../contexts/SettingsContext';



const schema = yup.object({
  title: yup.string().required('Proposal title is required').min(2, 'Title must be at least 2 characters').max(200, 'Title must be less than 200 characters'),
  customerId: yup.string().required('Customer is required'),
  templateId: yup.string().nullable(),
  content: yup.string().required('Proposal content is required').min(10, 'Content must be at least 10 characters')
});

// Removed stepper steps - using single page form

const ProposalForm = ({ proposal, onSubmit, onCancel }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { settings, formatCurrency } = useSettings();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!id && !proposal);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  const isEdit = !!id || !!proposal;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    setValue,
    trigger
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      customerId: '',
      templateId: '',
      content: ''
    }
  });

  const watchedContent = watch('content');
  const watchedCustomerId = watch('customerId');
  const watchedTemplateId = watch('templateId');

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load data sequentially to avoid overwhelming the connection pool
        await fetchCustomers();
        await new Promise(resolve => setTimeout(resolve, 100)); // Small delay
        await fetchTemplates();
        
        if (isEdit && !proposal) {
          await new Promise(resolve => setTimeout(resolve, 100)); // Small delay
          await fetchProposal();
        }
      } catch (error) {
        console.error('Error loading form data:', error);
      }
    };
    
    loadData();
  }, [id, isEdit, proposal]);

  useEffect(() => {
    if (proposal) {
      reset({
        title: proposal.title || '',
        customerId: proposal.customerId || '',
        templateId: proposal.templateId || '',
        content: proposal.content || ''
      });
    }
  }, [proposal, reset]);

  // Removed total amount calculation - no longer needed

  useEffect(() => {
    // Load template content when template is selected
    if (watchedTemplateId && !isEdit) {
      loadTemplateContent(watchedTemplateId);
    }
  }, [watchedTemplateId, isEdit]);

  const fetchCustomers = async () => {
    try {
      const response = await customersAPI.getCustomers({ limit: 1000 });
      setCustomers(response.data.customers);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to fetch customers');
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await templatesAPI.getProposalTemplates({ limit: 1000, isActive: true });
      setTemplates(response.data.templates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to fetch templates');
    }
  };

  const fetchProposal = async () => {
    try {
      setInitialLoading(true);
      const response = await proposalsAPI.getById(id);
      const proposal = response.data.proposal;
      
      reset({
        title: proposal.title,
        customerId: proposal.customerId,
        templateId: proposal.templateId || '',
        content: proposal.content
      });
    } catch (error) {
      console.error('Error fetching proposal:', error);
      toast.error('Failed to fetch proposal');
      navigate('/proposals');
    } finally {
      setInitialLoading(false);
    }
  };

  const loadTemplateContent = async (templateId) => {
    try {
      const response = await templatesAPI.getProposalTemplate(templateId);
      const template = response.data.template;
      setValue('content', template.content);
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Failed to load template content');
    }
  };

  const handleFormSubmit = async (data) => {
    // Convert empty string templateId to null for server validation
    const formData = {
      ...data,
      templateId: data.templateId === '' ? null : data.templateId
    };

    if (onSubmit) {
      onSubmit(formData);
    } else {
      try {
        setLoading(true);
        
        if (isEdit) {
          await proposalsAPI.update(id, formData);
          toast.success('Proposal updated successfully');
        } else {
          await proposalsAPI.create(formData);
          toast.success('Proposal created successfully');
        }
        
        navigate('/proposals');
      } catch (error) {
        console.error('Error saving proposal:', error);
        toast.error(`Failed to ${isEdit ? 'update' : 'create'} proposal`);
      } finally {
        setLoading(false);
      }
    }
  };

  // Removed stepper navigation functions

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      if (isDirty) {
        if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
          navigate('/proposals');
        }
      } else {
        navigate('/proposals');
      }
    }
  };

  // Removed item management functions

  // Removed handleSendEmail function



  if (initialLoading) {
    return <LoadingSpinner />;
  }

  // Removed renderStepContent function - using single form layout

  return (
    <Box sx={{ pl: 3 }}>
      {/* Header */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: { xs: 2, md: 0 }
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {isEdit ? 'Edit Proposal' : 'Create New Proposal'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isEdit ? 'Update proposal information and content' : 'Create a new proposal for your customer'}
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          flexWrap: 'wrap',
          width: { xs: '100%', md: 'auto' },
          justifyContent: { xs: 'flex-start', md: 'flex-end' }
        }}>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={() => setPreviewOpen(true)}
            disabled={!watchedContent}
            size="small"
          >
            Preview
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            size="small"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<SaveIcon />}
            size="small"
            form="proposal-form"
          >
            {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
          </Button>
        </Box>
      </Box>

      <form id="proposal-form" onSubmit={handleSubmit(handleFormSubmit)}>
            <Grid container spacing={3}>
              {/* Title Field */}
              <Grid item xs={12}>
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Proposal Title"
                      fullWidth
                      error={!!errors.title}
                      helperText={errors.title?.message}
                      placeholder="Enter proposal title"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AssignmentIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Customer Dropdown */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="customerId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.customerId}>
                      <InputLabel>Customer *</InputLabel>
                      <Select {...field} label="Customer *">
                        {customers.map((customer) => (
                          <MenuItem key={customer.id} value={customer.id}>
                            {customer.companyName}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.customerId && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                          {errors.customerId.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Template Dropdown */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="templateId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Template (Optional)</InputLabel>
                      <Select {...field} label="Template (Optional)">
                        <MenuItem value="">No Template</MenuItem>
                        {templates.map((template) => (
                          <MenuItem key={template.id} value={template.id}>
                            {template.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>



              {/* Rich Text Editor */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Proposal Content
                </Typography>
                <Controller
                  name="content"
                  control={control}
                  render={({ field }) => (
                    <TinyMCEEditor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Enter the proposal content here. If you selected a template, its content will appear here for editing."
                      height={800}
                      error={!!errors.content}
                      helperText={errors.content?.message}
                    />
                  )}
                />
              </Grid>


            </Grid>


        </form>

        {/* Preview Dialog */}
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { height: '80vh' }
          }}
        >
          <DialogTitle>
            Proposal Preview
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                backgroundColor: 'background.paper',
                height: '100%',
                overflow: 'auto'
              }}
              dangerouslySetInnerHTML={{ __html: watchedContent }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
};

export default ProposalForm;